import React, { useEffect, useRef, useState } from "react"
import tw from "twin.macro"
import useTealiumEvent from "../../../hooks/Tealium/useTealiumEvent"
import { VideoS3Props } from "./Video.d"
import { Image } from "../../atoms/Image"
import Play from "../../atoms/Icon/Play"

const VideoS3: React.FC<VideoS3Props> = ({
  videoSectionHeading,
  video_s3,
  overlayImage,
  overlayImageStyles,
  autoPlay = false,
  extraTealiumValues,
  ...remainingProps
}) => {
  const videoS3El = useRef(null)
  const { trackTealEvent } = useTealiumEvent()
  const [active, setActive] = useState(autoPlay) // Set initial state based on autoPlay
  const [isVideoVisible, setIsVideoVisible] = useState(true) // Track video visibility

  let pauseDueToFocusLoss = false // Flag to track focus-related pause events
  const percentWatched = () => {
    const percentage = Math.round(
      (videoS3El?.current?.currentTime! / videoS3El?.current?.duration!) * 100,
    )

    return percentage
  }
  // Intersection Observer to track video visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVideoVisible(entry.isIntersecting) // Update visibility state
      },
      { threshold: 0.5 }, // Trigger when 50% of the video is visible
    )

    if (videoS3El.current) {
      observer.observe(videoS3El.current)
    }

    return () => {
      if (videoS3El.current) {
        observer.unobserve(videoS3El.current)
      }
    }
  }, [])

  const handlePlay = () => {
    const currentTime = Math.round(videoS3El?.current?.currentTime).toString()
    const isFocused = document.hasFocus() // Check if the browser is focused
    // Start event
    if (!isVideoVisible) {
      console.log("Start event ignored because video is not visible.")
      return
    }
    // Start event
    if (pauseDueToFocusLoss) {
      console.log("Start event after focus loss. Ignoring event.")
      pauseDueToFocusLoss = false // Reset the flag
      return
    } else if (autoPlay && currentTime === "0") {
      console.log("Start event due to autoplay. Ignoring event.")
      return
    } else if (isFocused) {
      tealVideoEvent("start")
    }
  }

  const handlePause = () => {
    const videoWrapper = document.getElementById("video-s3")

    const isHidden = document.hidden // Check if the browser is focused
    const videoIsVisible = videoWrapper?.checkVisibility() // Check if the video is visible

    // Pause event
    if (isHidden || !isVideoVisible) {
      console.log("Pause due to focus loss. Ignoring event.")
      pauseDueToFocusLoss = true // Mark that the pause was due to focus loss
      return
    } else if (
      videoS3El?.current?.currentTime !== videoS3El?.current?.duration
    ) {
      tealVideoEvent("pause")
    }
  }

  const tealVideoEvent = (action: string, time?: string) => {
    const timeOnPause = `${percentWatched()}:${Math.round(
      videoS3El?.current?.currentTime,
    )}`

    trackTealEvent({
      tealium_event: "video_event",
      video_type: `${`${videoSectionHeading}-${video_s3?.asset?._id}`}|${action}|${
        time ? time : timeOnPause
      }`,
      ...(extraTealiumValues && extraTealiumValues),
    })
  }

  return (
    <div
      onClick={() => {
        if (videoS3El?.current) {
          setActive(true)
        }
      }}
      id={"video-s3"}
      css={[tw`p-12`]}
    >
      <video
        ref={videoS3El}
        id={video_s3?.asset?._id}
        controls={true}
        title={videoSectionHeading}
        src={`${process.env.GATSBY_ASSET_URL}${video_s3?.asset?.s3?.key}`}
        autoPlay={autoPlay}
        muted={autoPlay}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={() => {
          !autoPlay &&
            tealVideoEvent(
              "ended",
              `100:${Math.round(videoS3El?.current?.duration || 0)}`,
            )
        }}
        css={[tw`h-3/4 w-full`, tw`lg:(mx-auto h-5/6 w-auto)`]}
        loop
        {...remainingProps}
      ></video>
      {overlayImage && !active && !autoPlay && (
        <div>
          <Image
            imageData={overlayImage}
            css={[
              tw`absolute top-0 left-0 object-cover w-full h-full`,
              overlayImageStyles,
            ]}
          />
          <Play
            css={tw`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2`}
          />
        </div>
      )}{" "}
    </div>
  )
}

export default VideoS3
